import $ from 'jquery';
import _ from 'underscore';
import Constants from 'core/constants';
import BaseController from 'controllers/base';
import DashboardPageableModel, {
  DashboardCollection,
} from 'collections/clients/dashboard';
import DashboardModel from 'models/clients/dashboard';
import DashboardCopyModel from 'models/clients/dashboards/copy';
import DashboardShareModel from 'models/clients/dashboards/share';
import DashboardCardPageableModel, {
  DashboardCardsCollection,
} from 'collections/clients/dashboards/cards';
import DashboardCardModel from 'models/clients/dashboards/card';
import DashboardCardCopyModel from 'models/clients/dashboards/card/copy';
import GenerateRoute from 'core/utils/generateRoute';
import { cloneDeep } from 'lodash';
import getDatePickerButtons from 'core/utils/get-date-picker-buttons';
import { supportsDateTime } from 'views/panels/dashboards/date-type';
import addDimensionForKPI from 'core/utils/add-dimension-for-kpi';
import {
  DATA_SOURCE_DASHBOARDS_CARDS,
  DATA_SOURCE_DASHBOARDS,
} from 'views/panels/data-sources/queries';
import { getDefaultBenchmarkKeys } from 'views/panels/data-sources/utils/benchmarkKeys';
import calendarLookup from 'views/widgets/calendar/calendar-lookup';

const { HOLLOW_DASHBOARD_TYPE, DASHBOARD } = Constants;
const shallowClone = (x) => Object.assign({}, x);

class DashboardsController extends BaseController {
  constructor() {
    super(arguments, {
      actions: {
        'dashboards:get': '_get',
        'dashboards:set': '_set',
        'dashboards:changeOwner': '_changeDashboardOwner',
        'dashboards:sortOrder:put': '_saveSortOrders',
        'dashboards:cards:sortOrder:put': '_saveCardSortOrders',
        'dashboards:hidden:put': '_toggleDashboardHidden',
        'dashboards:post': '_post',
        'dashboards:put': '_put',
        'dashboards:delete': '_delete',
        'dashboards:copy': '_copyDashboard',
        'dashboards:share': '_share',
        'dashboards:shares:get': '_getShares',
        'dashboards:shares:getMultiple': '_fetchDashboardsShares',
        'dashboards:shares:put': '_updateShare',
        'dashboards:shares:delete': '_deleteShare',
        'dashboards:cards:get': '_getCards',
        'dashboards:cards:set': '_setCards',
        'dashboards:cardDetail:action:set': '_setCardDetailAction',
        'dashboards:cardDetail:action:setRoute': '_updateCardDetailActionRoute',
        'dashboards:cardDetail:set': '_setCardDetails',
        'dashboards:cardDetail:get': '_getCardDetails',
        'dashboards:card:data:set': '_setCardData',
        'dashboards:card:setSize': '_setCardSize',
        'dashboards:cardDetail:lookupDate': '_setDateRange',
        'dashboards:cardDetail:setEventFilter': '_setEventFilter',
        'dashboards:cardDetail:setBDFilter': '_setBDFilter',
        'dashboards:drillIn': '_drillIn',
        'dashboards:card:new': '_initNewCard',
        'dashboards:card:post': '_postCard',
        'dashboards:card:copy': '_copyCard',
        'dashboards:card:delete': '_deleteCard',
        'dashboards:card:attributes:put': '_updateAttributes',
        'dashboards:measures:reload': '_reloadMeasureDashCards',
        'dashboard:data:get': '_getDashboardData',
        'dashboard:cards:update': '_updateDashboardCards',
      },
      name: 'dashboards',
      dependsOn: ['user', 'authorization', 'dashboards-data', 'export'],
    });
  }

  initialize(opts) {
    const options = opts || {};
    options.clientId = this.getClientId();
  }

  _fetchDashboardPage(payload, page) {
    const chunkSize = 25; // TODO increase this to 100 when we get closer to release. Leaving at 25 to make testing easier
    const { externalType, externalId, callback } = payload;

    const model = new DashboardPageableModel(false, {
      clientId: this.getClientId(),
      externalType,
      externalId: externalType === externalId,
      limit: chunkSize,
      offset: chunkSize * page,
    });
    model
      .fetch({
        error: (...args) => {
          //TODO
        },
        doNotAbort: payload.doNotAbort,
      })
      .then(() => {
        if (page === 0) {
          this.dashboards = [];
        }
        this.dashboards = this.dashboards.concat(
          model.toJSON().results.map((d) => {
            delete d.cards;
            return d;
          })
        );
        if (
          model.get('totalElements') > this.dashboards.length &&
          model.changed.hasMore
        ) {
          this._fetchDashboardPage(payload, page + 1);
        } else {
          this.publish('authorization:users:get', {
            internal: true,
            ids: _.compact(
              this.dashboards.map((d) => {
                return d.ownerId;
              })
            ),
            callback: (users) => {
              this.dashboards.forEach((d) => {
                const user = _.findWhere(users, { userId: d.ownerId });
                d.ownerUser = user;
              });
              this.publish('app:updateData', {
                dashboards: this.dashboards,
                callback,
              });
            },
          });
        }
      });
  }

  _get(payload) {
    if (this.getClientId() === -1) {
      this.publish('app:updateData', {
        dashboards: [],
        callback: payload.callback,
      });
      return;
    }
    if (!payload.externalType) {
      this.publish('app:updateData', {
        dashboards: false,
      });
    }
    this._fetchDashboardPage(payload, 0);
  }

  _getUsersAndRoles(userIds, roleIds) {
    const defs = [];
    const userDef = $.Deferred();

    if (userIds.length) {
      this.publish('authorization:users:get', {
        internal: true,
        ids: userIds,
        limit: 100,
        callback: (users) => {
          userDef.resolve(users);
        },
      });
    } else {
      userDef.resolve([]);
    }
    defs.push(userDef);
    const roleDef = $.Deferred();
    if (roleIds.length) {
      this.publish('authorization:roles:get', {
        callback: (roles) => {
          roleDef.resolve(
            roleIds.map((roleId) => {
              return _.findWhere(roles, { roleId });
            })
          );
        },
      });
    } else {
      roleDef.resolve([]);
    }
    defs.push(roleDef);
    return $.when.apply($, defs);
  }

  _fetchDashboardsShares(dashboards) {
    const getShare = async (dashboardId, clientId, offset) => {
      const model = new DashboardShareModel(false, {
        dashboardId,
        clientId: this.getClientId(),
        offset,
      });

      const modelResult = await model.fetch({ error: () => {} });

      const { hasMore } = modelResult;
      if (hasMore) {
        const recResult = await getShare(dashboardId, clientId, offset + 1);
        return {
          dashboardId,
          ...modelResult,
          shares: [...modelResult.shares, ...recResult.shares],
        };
      }

      return {
        dashboardId,
        ...modelResult,
        shares: [...modelResult.shares],
      };
    };

    const promises = dashboards.map(async (d) =>
      getShare(d.dashboardId, this.getClientId(), 0)
    );

    const that = this;
    Promise.all(promises).then((newShares) => {
      that.publish('app:getData', {
        key: 'dashboardShares',
        callback: (dashboardShares) => {
          const shares = dashboardShares || {};

          const userIds = [];
          const roleIds = [];
          newShares.forEach((d) => {
            d.shares.forEach((s) => {
              if (s.shareType === 'USER') {
                userIds.push(s.userId);
              } else if (s.shareType === 'ROLE') {
                roleIds.push(s.roleId);
              }
            });
          });

          that
            ._getUsersAndRoles(_.uniq(userIds), _.uniq(roleIds))
            .done((users, roles) => {
              newShares.forEach((d) => {
                const dShares = [];

                d.shares.forEach((s) => {
                  if (s.shareType === 'USER') {
                    s.metadata = _.findWhere(users, { userId: s.userId });
                  } else if (s.shareType === 'ROLE') {
                    s.metadata = _.findWhere(roles, { roleId: s.roleId });
                  } else if (s.shareType === 'CLIENT') {
                    s.metadata = {
                      name: that.getString('general.public'),
                      description: that.getString(
                        'dashboards.sharing.access.public'
                      ),
                    };
                  }

                  if (s.metadata) {
                    dShares.push(s);
                  }
                });
                shares[d.dashboardId] = dShares;
              });
              that.publish('app:updateData', { dashboardShares: shares });
            });
        },
      });
    });
  }

  _put(payload, doNotRefetch) {
    const { dashboardId, ownerChanged } = payload;
    const model = new DashboardModel(payload, {
      clientId: this.getClientId(),
      id: dashboardId,
    });
    model.save().then(() => {
      const options = {};
      if (ownerChanged) {
        options.callback = () => {
          this.changeRoute('dashboards.base');
        };
      }
      if (!doNotRefetch) {
        options.doNotAbort = true;
        this._get(options);
      }
      if (payload.callback) {
        payload.callback();
      }
    });
  }

  _delete(payload) {
    const { dashboardId, currentDashboardId } = payload;
    this.publish('app:updateState', { deletingDashboard: true });
    const model = new DashboardModel(
      {
        dashboardId: dashboardId,
      },
      {
        clientId: this.getClientId(),
        id: dashboardId,
      }
    );
    model.destroy({
      success: () => {
        this.publish('app:updateState', {
          deleteDashboard: false,
          deletingDashboard: false,
        });
        this._get({
          doNotAbort: true,
          callback: () => this._updateUnifiedNav(),
        });
        // reload dashboard view if the selected dashboard is the one being deleted
        if (`${currentDashboardId}` === `${dashboardId}`) {
          this.changeRoute('dashboards.base');
        }
        this.publish('app:messageFlash', {
          messageText: this.getString('dashboards.snackbar.deleteDashboard'),
          messageType: 'info',
        });
      },
    });
  }

  _share(payload, isNew) {
    const {
      dashboardId,
      accessType,
      shareType,
      userId,
      roleId,
      notification,
      notificationMessage,
      callback,
    } = payload;
    const model = new DashboardShareModel(
      {
        accessType: accessType || 'READ',
        clientId: this.getClientId(),
        shareType: shareType || '',
        hidden: false,
        notification: notification,
        notificationMessage: notificationMessage || '',
        userId: userId,
        roleId: roleId,
      },
      {
        dashboardId,
        clientId: this.getClientId(),
      }
    );
    model
      .save({
        error: () => {},
      })
      .then((data) => {
        if (isNew) {
          this._addDashboard(payload);
        }
        if (callback) {
          callback(data);
        } else {
          this._get({});
          this._fetchDashboardsShares([{ dashboardId }]);
        }
      });
  }

  //TODO see where this gets called...can this be deprecated? in favour of _fetchDashboardsShares
  _getShares(payload) {
    const { dashboardId } = payload;
    const model = new DashboardShareModel(false, {
      dashboardId,
      clientId: this.getClientId(),
    });
    model
      .fetch({
        error: () => {},
      })
      .then(() => {
        this.publish('app:getData', {
          key: 'dashboardShares',
          callback: (dashboardShares) => {
            const shares = dashboardShares || {};
            shares[dashboardId] = model.toJSON().shares;
            this.publish('app:updateData', { dashboardShares: shares });
          },
        });
      });
  }

  _updateShare(share) {
    const { shareId, dashboardId } = share;
    const model = new DashboardShareModel(share, {
      dashboardId,
      shareId,
      clientId: this.getClientId(),
    });

    //No loading state here, immediately update app data with new share
    this.publish('app:getData', {
      key: 'dashboardShares',
      callback: (dashboardShares) => {
        const shares = dashboardShares || {};
        shares[dashboardId] = shares[dashboardId].map((s) => {
          if (s.shareId === shareId) {
            return share;
          }
          return s;
        });
        this.publish('app:updateData', { dashboardShares: shares });
      },
    });

    model.save().then(() => {
      this._fetchDashboardsShares([{ dashboardId }]);
    });
  }

  _deleteShare(payload) {
    const { shareId, dashboardId, callback } = payload;
    const model = new DashboardShareModel(
      {
        shareId,
      },
      {
        shareId,
        dashboardId,
        clientId: this.getClientId(),
      }
    );
    model.destroy({
      success: () => {
        this._fetchDashboardsShares([{ dashboardId }]);
        if (callback) {
          callback({ shareId, dashboardId });
        }
      },
      // error: ,
      // doNotAbort: true
    });
  }

  _updateDashboardCard(dashboardId, card, callback) {
    if (card.dataSourceId) {
      this.publish('dashboards-data:card:rest:set', {
        cardData: card,
      });
    }
    this.publish('app:getData', {
      key: 'dashboardCards',
      callback: (allDashboardCards) => {
        const dashboardCards = allDashboardCards || {};
        const existingCards = dashboardCards[dashboardId];
        if (existingCards) {
          const index = _.findIndex(existingCards, { cardId: card.cardId });
          if (index >= 0) {
            existingCards[index] = card;
          } else {
            console.log(`Card ${card.cardId} not found`);
          }
        }
        this.publish('app:updateData', { dashboardCards, callback });
      },
    });
  }

  _updateDashboardCards(dashboardId, cards, callback) {
    this.publish('app:getData', {
      key: 'dashboardCards',
      callback: (dashboardCards) => {
        dashboardCards = dashboardCards || {};
        dashboardCards[dashboardId] = cards;
        this.publish('app:updateData', { dashboardCards, callback });
      },
    });
  }

  _getDashboard(dashboardId, callback) {
    this.publish('app:getData', {
      key: 'dashboards',
      callback: (dashboards) => {
        const dashboard = _.find(dashboards, (d) => {
          return d.dashboardId.toString() === dashboardId.toString();
        });
        callback(dashboard);
      },
    });
  }

  _getCards(payload) {
    const {
      dashboardId,
      dateRange,
      externalType,
      externalId,
      dashboardType,
      shouldFetchDashboardData = true,
      filters,
      callback,
    } = payload;
    this._updateDashboardCards(dashboardId, false);
    const model = new DashboardCardPageableModel(false, {
      clientId: this.getClientId(),
      dashboardId,
    });

    model
      .fetch({
        error: () => {
          //TODO
        },
        doNotAbort: true,
      })
      .then(() => {
        const { cards } = model.toJSON();
        cards.forEach((card) => this._sanitizeDateRange(card));
        this._updateDashboardCards(dashboardId, cards, () => {
          if (callback) {
            callback(cards);
          }
          this._fetchDashboardsShares([{ dashboardId }]);
        });
      });
  }

  _getDashboardData(cards) {
    this.publish('dashboards-data:dashboard:get', { cards });
  }

  _set(payload) {
    this.publish('app:updateData', {
      dashboards: payload.dashboards,
      callback: payload.callback,
    });
  }

  _toggleDashboardHidden(payload) {
    this.publish('app:getData', {
      key: 'dashboards',
      callback: (dashboards) => {
        dashboards.forEach((d, index) => {
          if (d.dashboardId === payload.dashboardId) {
            d.hidden = !d.hidden;
            d.callback = () => this._updateUnifiedNav();
            this._put(d, true);
          }
        });
        this.publish('app:updateData', { dashboards });
      },
    });
  }

  _updateUnifiedNav() {
    window.dispatchEvent(new CustomEvent('xm-navigator:fetch-trigger'));
  }

  _saveSortOrders(payload) {
    this.publish('app:getData', {
      key: 'dashboards',
      callback: (dashboards) => {
        if (dashboards) {
          const sortedDashboards = dashboards.map((dashboard, index) => {
            return {
              ...dashboard,
              sortOrder: index,
            };
          });
          const model = new DashboardCollection(sortedDashboards, {
            clientId: this.getClientId(),
          });
          model
            .save({
              error: () => {},
            })
            .then(() => {
              this._updateUnifiedNav();
            });
        }
      },
    });
  }

  _saveCardSortOrders(payload) {
    const { dashboardId, newCardOrder = {} } = payload;

    this.publish('app:getData', {
      key: 'dashboardCards',
      callback: (allDashboardsCards) => {
        const dashboardCards = allDashboardsCards[dashboardId] || [];
        if (dashboardCards) {
          const sortedCards = dashboardCards.map((card, index) => {
            return {
              ...card,
              sortOrder:
                newCardOrder[card.cardId] !== undefined
                  ? newCardOrder[card.cardId]
                  : index,
            };
          });

          this._saveCards({ cards: sortedCards, dashboardId });
        }
      },
    });
  }

  _setCards(payload) {
    const { dashboardId, cards, callback } = payload;
    this._updateDashboardCards(dashboardId, cards, callback);
  }

  _getAttribute(card, key) {
    if (card && card.metadata && card.metadata.attributes) {
      return card.metadata.attributes[key];
    }
    return null;
  }

  _setCardData(payload) {
    const { dashboardId, cardId, data, callback } = payload;
    this.publish('app:getData', {
      key: 'dashboards',
      callback: (dashboards) => {
        const d = _.findWhere(dashboards, { dashboardId });
        const c = _.findWhere(d.cards, { cardId });
        c.data = data;
        this.publish('app:updateData', { dashboards, callback });
      },
    });
  }

  _reloadData(isInitialization) {
    if (isInitialization) {
      return;
    }
    this.publish('app:getData', {
      key: 'cardDetail',
      callback: (cardDetail) => {
        this.publish('dashboards-data:card:get', cardDetail);
      },
    });
  }

  async _adjustDateRangeForDataSource(payload, dataSources) {
    // if the date source has changed from one that used dateTime to one that
    // uses date, make sure the time parts are removed
    const adjustedPayload = _.clone(payload);
    const chartType = await this._getCurrentCardAttribute('chartType');
    if (supportsDateTime(chartType, dataSources)) {
      adjustedPayload.dateRangeType = 'DATE_TIME';
    } else if (payload.dateRangeType !== 'DATE_ONLY') {
      if (payload.startDate) {
        adjustedPayload.startDate = payload.startDate.format('YYYY-MM-DD');
      }
      if (payload.endDate) {
        adjustedPayload.endDate = payload.endDate.format('YYYY-MM-DD');
      }
      adjustedPayload.dateRangeType = 'DATE_ONLY';
    }
    return adjustedPayload;
  }

  async _setDateRange(payload) {
    const dataSources = await this._getCurrentCardAttribute('dataSources');
    const adjustedPayload = await this._adjustDateRangeForDataSource(
      payload,
      dataSources
    );
    let range;

    const cal = await calendarLookup({
      calendarType: payload.calendarType,
      clientId: payload.clientId,
      dateType: payload.dateType,
      rangeType: payload.rangeType,
      periodType: payload.periodType,
      number: payload.number,
      asOf: payload.asOf,
      first: payload.first || payload.start_date,
      last: payload.last || payload.end_date,
      timezone: payload.timezone,
    });
    range = cal.data;

    if (range.c !== adjustedPayload.calendarType) {
      range.c = adjustedPayload.calendarType;
    }
    delete range.a;

    this._updateAttributes({
      key: 'dateRange',
      value: range,
      callback: () => {
        this._updateAttributes({
          key: 'selectedDateId',
          value: payload.id || 'custom',
          callback: this._reloadData.bind(this, payload.isInitialization),
        });
      },
    });
  }

  _setEventFilter({
    noReload = false,
    isInitialization,
    gdfContext = undefined,
  }) {
    this._updateAttributes({
      key: 'eventFilterId',
      callback: !noReload
        ? this._reloadData.bind(this, isInitialization, gdfContext)
        : () => {},
    });
  }

  _setBDFilter({ filters, gdfContext, noReload = false }) {
    const hasLabelsFilter =
      filters?.some((f) =>
        f.conditionGroup?.conditions?.some(
          (c) => c.subType === 'RESPONDENT_LABELS'
        )
      ) || undefined;
    this._updateAttributes({
      changedAttributes: {
        filters:
          filters &&
          filters.map((filter) => {
            return {
              type: filter.filterMetaData.type,
              key: filter.filterId,
            };
          }),
        hasLabelsFilter,
      },
      callback: !noReload
        ? this._reloadData.bind(this, false, gdfContext)
        : () => {},
    });
  }

  _setCardDetails(payload) {
    const { card, cardDetailData, externalType, externalId, externalRouteKey } =
      payload;

    const cardClone = card && JSON.parse(JSON.stringify(card));
    const cardDetailDataClone =
      cardDetailData && JSON.parse(JSON.stringify(cardDetailData));
    const { dashboardId, cardId } = cardClone;

    this.publish('app:updateData', {
      cardDetail: cardClone,
      cardDetailData: {
        ...cardDetailDataClone,
        cardId,
      },
      callback: () => {
        if (externalType === 'DATASOURCES') {
          this.changeRoute('datasources.card', {
            dataSourceType: cardClone.dataSourceType,
            dataSourceKey: externalId,
            dashboardId,
            cardId,
            schemaId:
              cardClone?.metadata?.attributes?.dataSources?.[0]?.schemaId,
            filterId: cardClone?.filterId || '',
          });
        } else {
          this.changeRoute('dashboards.card', { dashboardId, cardId });
        }
      },
    });
  }

  _setCardDetailAction(payload) {
    const { card, cardDetailData, action } = payload;
    const cardClone = card ? JSON.parse(JSON.stringify(card)) : undefined;
    const cardDetailDataClone = cardDetailData
      ? JSON.parse(JSON.stringify(cardDetailData))
      : undefined;

    this.publish('app:updateData', {
      cardDetail: cardClone,
      cardDetailData: {
        ...cardDetailDataClone,
        cardId: cardClone.cardId,
      },
      callback: () => {
        const { cardId, dashboardId } = cardClone;
        this._updateCardDetailActionRoute({
          cardId,
          dashboardId,
          action,
          card: cardClone,
        });
      },
    });
  }

  _updateCardDetailActionRoute(payload) {
    const { cardId, dashboardId, card } = payload;
    const routeMap = {
      card: 'card',
      edit: 'editCard',
      move: 'moveCard',
      copy: 'copyCard',
      delete: 'deleteCard',
    };
    const action = payload.action || 'card';
    this.publish('route:get', {
      callback: (payload) => {
        const { route, pathIds } = payload;
        const { analytics } = pathIds;
        let routeType = 'dashboards';
        let routeChangePayload = {
          dashboardId,
          cardId,
        };
        if (analytics) {
          routeChangePayload.type = analytics;
        }
        if (pathIds.datasources && card) {
          routeType = 'datasources';
          routeChangePayload.dataSourceType = card.dataSourceType;
          routeChangePayload.dataSourceKey = card.dataSourceId;
          routeChangePayload.dashboardId = card.dashboardId;
          routeChangePayload.cardId = card.cardId;
          routeChangePayload.schemaId =
            card?.metadata?.attributes?.dataSources[0]?.schemaId;
          routeChangePayload.filterId =
            card?.metadata?.attributes?.filters?.[0]?.key;
        }
        this.changeRoute(
          `${routeType}.${routeMap[action]}`,
          routeChangePayload
        );
      },
    });
  }

  _getRoutePayload({ metadata }) {
    const { attributes } = metadata;
    const dataSourceType = attributes.dataSources[0].type;

    return {
      dataSourceKey: attributes.dataSources[0].key,
      dataSourceType,
      dateRange: attributes.dateRange
        ? JSON.stringify(attributes.dateRange)
        : '',
    };
  }

  _getCardDetails(payload) {
    const {
      dashboardId,
      cardId,
      externalType,
      externalId,
      autoFetch,
      metadata,
      overrideDateRange,
      overrideDateRangeCallback,
      previousDateRange,
      previousSelectedDateId,
      dataSourceType,
      currentDashboard,
      dataSourceId,
      schemaId,
    } = payload;

    let filters = metadata?.attributes?.filters || [];

    if (filters.length === 0 && dataSourceType) {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const filterId = urlParams.get('filterId');
        if (filterId) {
          filters = [
            {
              key: filterId,
              type: 'BD_FILTER',
            },
          ];
        }
      } catch (err) {
        console.error('Error parsing filterId from URL:', err);
      }
    }

    if (isNaN(parseInt(cardId, 10))) {
      this._get({
        callback: () => {
          this._getCards({
            dashboardId,
            callback: this._initNewCard.bind(this, {
              dashboardId,
              chartType: cardId,
            }),
          });
        },
      });
      return;
    }

    const model = new DashboardCardModel(false, {
      clientId: this.getClientId(),
      dashboardId,
      cardId,
    });

    model
      .fetch({
        error: () => {
          //TODO
        },
      })
      .then(async () => {
        const card = model.toJSON();
        const chartType = this._getAttribute(card, 'chartType');
        if (
          chartType === Constants.DASHBOARD.chartTypes.external &&
          !this.hasFeature('has-iframe-card')
        ) {
          return false;
        }

        if (dataSourceType) {
          const { data } = await this.apolloClient.query({
            query: DATA_SOURCE_DASHBOARDS_CARDS,
            variables: {
              clientId: this.getClientId(),
              dashboardId,
              schemaId,
              dataSourceType,
              dataSourceId,
              cardId,
            },
          });
          let preparedCurrentDashboard;
          if (!currentDashboard) {
            const { data: dataSourceDashboards } =
              await this.apolloClient.query({
                query: DATA_SOURCE_DASHBOARDS,
                variables: {
                  clientId: this.getClientId(),
                  dataSourceType,
                  dataSourceId,
                  schemaId,
                },
              });

            preparedCurrentDashboard =
              dataSourceDashboards.dataSourceDashboards.find(
                (d) => d.dashboardId === dashboardId
              );
          }

          const gqlCardData = data.getPreparedDashboardCards[0];

          let dateRange = previousDateRange;
          let selectedDateId = previousSelectedDateId;

          if (!dateRange) {
            try {
              const dataSourcesDateRangeStr = window.localStorage.getItem(
                'dataSourcesDateRange'
              );
              if (dataSourcesDateRangeStr) {
                const dataSourcesDateRange = JSON.parse(
                  dataSourcesDateRangeStr
                );
                if (
                  dataSourcesDateRange &&
                  dataSourcesDateRange.f &&
                  dataSourcesDateRange.l
                ) {
                  dateRange = dataSourcesDateRange;
                  selectedDateId = 'custom';
                }
              }
            } catch (err) {
              console.error(
                'Error parsing dataSourcesDateRange from localStorage:',
                err
              );
            }
          }

          const filledCardTemplate = {
            ...gqlCardData,
            cardId,
            dataSourceType,
            dataSourceId,
            currentDashboard: currentDashboard || preparedCurrentDashboard,
            metadata: {
              ...gqlCardData.metadata,
              attributes: {
                ...gqlCardData.metadata.attributes,
                dateRange: dateRange || card.metadata.attributes.dateRange,
                selectedDateId,
                benchmarkKeys: getDefaultBenchmarkKeys(
                  gqlCardData?.metadata?.attributes?.benchmarkType,
                  gqlCardData?.metadata?.attributes?.dataSources
                ),
                filters,
              },
            },
          };

          this.publish('app:updateData', {
            cardDetail: filledCardTemplate,
          });
          this.publish('dashboards-data:card:get', {
            ...filledCardTemplate,
            autoFetch,
            isOnDetail: true,
          });
        } else {
          let updatedCard = card;

          // Apply dataSources from current card when it's not available in card model
          if (overrideDateRange) {
            const { dateRange, selectedDateId } = card.metadata.attributes;

            // when overriding the date range send back what the original date
            // range was; this is so global date state can have the right info
            if (overrideDateRangeCallback) {
              overrideDateRangeCallback(dateRange, selectedDateId);
            }

            updatedCard = {
              ...updatedCard,
              metadata: {
                ...updatedCard.metadata,
                attributes: {
                  ...updatedCard.metadata.attributes,
                  dateRange: overrideDateRange,
                },
              },
            };
          }

          this.publish('app:updateData', { cardDetail: updatedCard });
          this.publish('dashboards-data:card:get', {
            ...updatedCard,
            autoFetch,
            isOnDetail: true,
          });
        }
      });
  }

  _addDashboard(newDash) {
    this._get({
      callback: () => {
        this._updateUnifiedNav();
        this.changeRoute('dashboards.summary', {
          dashboardId: newDash.dashboardId,
        });
      },
    });
  }

  _saveDashboard(payload, sortOrder) {
    const { name, callback, onError } = payload;
    const cards = payload.cards || [];
    const record = {
      cards,
      name,
      type: 'PERSONAL',
      hidden: false,
      sortOrder: sortOrder,
      metadata: {
        attributes: {},
      },
    };
    const options = {
      clientId: this.getClientId(),
    };
    const model = new DashboardModel(record, options);
    model
      .save(null, {
        error: onError,
      })
      .then(() => {
        const data = model.toJSON();
        if (callback) {
          callback(data);
        }
        this._addDashboard(data);
      });
  }

  _post(payload) {
    const savePayload = (order) =>
      this._saveDashboard(
        {
          ...payload,
          callback: (newDash) => {
            if (payload.callback) {
              payload.callback(newDash);
            }
          },
        },
        order
      );

    const getDashboards = (callback) => {
      this.publish('app:getData', {
        key: 'dashboards',
        callback,
      });
    };

    getDashboards((dashboardsFirst) => {
      if (dashboardsFirst) {
        savePayload(dashboardsFirst.length + 1);
      } else {
        this._get({
          callback: () => {
            getDashboards((dashboardsSecond) => {
              savePayload((dashboardsSecond?.length || 0) + 1);
            });
          },
        });
      }
    });
  }

  _copyDashboard(payload) {
    this.publish('app:getData', {
      key: 'dashboards',
      callback: (dashboards) => {
        const { dashboardId, name, callback } = payload;
        const model = new DashboardCopyModel(false, {
          dashboardId,
          clientId: this.getClientId(),
        });
        model
          .save({
            error: () => {},
          })
          .then(() => {
            const newDash = {
              ...model.toJSON(),
              name: name,
              sortOrder: dashboards.length + 1,
            };
            this._put(
              {
                ...newDash,
                callback: () => {
                  callback(newDash);
                  this._addDashboard(newDash);
                },
              },
              true
            );
          });
      },
    });
  }

  _getDefaultAttributes(chartType) {
    if (chartType === Constants.DASHBOARD.chartTypes.priorityIndex) {
      return {
        dataSources: [],
        surveyTypeIcons: true,
      };
    } else if (chartType === Constants.DASHBOARD.chartTypes.priorityMap) {
      if (this.hasFeature('big-data-reporting-priority-map-multi')) {
        return { dataSources: [] };
      }
    } else if (
      chartType === Constants.DASHBOARD.chartTypes.verticalBar ||
      chartType === Constants.DASHBOARD.chartTypes.horizontalBar
    ) {
      return {
        showDataLabels: true,
        stackingType: 'normal',
        sortOrder: 'default',
      };
    } else if (chartType === Constants.DASHBOARD.chartTypes.trendLine) {
      return {
        calloutLimits: 'values',
        stackingType: 'normal',
      };
    } else if (chartType === Constants.DASHBOARD.chartTypes.donut) {
      return {
        showDataLabels: 'both',
        sortOrder: 'hl',
        centerMetricDisplay: true,
      };
    } else if (chartType === Constants.DASHBOARD.chartTypes.voc) {
      return {
        replayIndicator: false,
      };
    } else if (chartType === Constants.DASHBOARD.chartTypes.external) {
      return {
        showDataSources: false,
        contentType: 'iframe',
        height: 500,
      };
    } else if (chartType === Constants.DASHBOARD.chartTypes.kpi) {
      return {
        delta: 'true',
        changePeriod: ['lastperiod'],
        sparkline: Constants.DASHBOARD.kpi.sparkline.bar,
      };
    } else if (chartType === Constants.DASHBOARD.chartTypes.scatterPlot) {
      return {
        metricDisplay: 'minMax',
      };
    }
    return {};
  }

  _initNewCard(payload) {
    const { dashboardId, chartType, cardSize, sortOrder, callback } = payload;
    const { external, priorityIndex } = Constants.DASHBOARD.chartTypes;
    this.publish('app:getData', {
      key: 'dashboardCards',
      callback: async (dashboardCards) => {
        dashboardCards = dashboardCards || {};
        const notExternalChart = chartType !== external;
        const defaultDateId =
          chartType === priorityIndex ? 'lastMonth' : 'monthToDate';
        const selectedDateId = notExternalChart
          ? { selectedDateId: defaultDateId }
          : {};
        const initialChartName =
          this.getString('general.untitled') +
          ' ' +
          this.getString(`dashboards.chartType.${chartType}`);
        const card = new DashboardCardModel({
          cardId: 0,
          sortOrder,
          clientId: this.getClientId(),
          dashboardId: dashboardId,
          name: initialChartName,
          metadata: {
            attributes: {
              chartType: chartType,
              cardSize: cardSize || 'medium',
              legendPosition: 'top',
              ...selectedDateId,
              ...this._getDefaultAttributes(chartType),
            },
          },
        });
        await this._asyncPublishAction('app:updateData', {
          cardDetail: card.toJSON(),
          cardDetailData: false,
        });
        await this._unsetAddCard();
        const number = chartType === 'priorityIndex' ? 1 : 0;
        if (notExternalChart) {
          this._setDateRange({
            calendarType: 'G',
            number: number,
            rangeType: 'M',
            selected: 3,
            id: defaultDateId,
            isInitialization: true,
          });
        }
        if (callback) {
          callback({ selectedDateId });
        }
      },
    });
  }

  async _unsetAddCard() {
    return this._asyncPublishAction('app:updateState', { addCard: false });
  }

  async _asyncPublishAction(action, payload = {}) {
    return new Promise((resolve) => {
      this.publish(action, { ...payload, callback: resolve });
    });
  }

  async _getCurrentCardAttribute(key, callback) {
    return new Promise((resolve) => {
      this.publish('app:getData', {
        key: 'cardDetail',
        callback: (card) => {
          const { metadata } = card || {};
          const { attributes } = metadata || {};
          resolve(attributes && attributes[key]);
        },
      });
    });
  }

  _updateAttributes(payload) {
    const { key, value, changedAttributes, callback } = payload;
    const toUpdate = changedAttributes || { [key]: value };

    this.publish('app:getData', {
      key: 'cardDetail',
      callback: (card) => {
        const { metadata } = card;
        const updatedCard = shallowClone(card);
        updatedCard.metadata = shallowClone(metadata);
        updatedCard.metadata.attributes = shallowClone(metadata.attributes);
        let hasUpdates = false;
        Object.keys(toUpdate).forEach((key) => {
          if (metadata.attributes[key] !== toUpdate[key]) {
            if (toUpdate[key] === undefined) {
              delete updatedCard.metadata.attributes[key];
            } else {
              updatedCard.metadata.attributes[key] = toUpdate[key];
            }
            hasUpdates = true;
          }
        });

        if (hasUpdates) {
          this.publish('app:updateData', {
            cardDetail: updatedCard,
            callback,
          });
        } else if (callback) {
          callback();
        }
      },
    });
  }

  _getSelectedDateIndex(dateType, rangeType, number) {
    const datePickerButtons = getDatePickerButtons(dateType);
    const selectedIndex = datePickerButtons.findIndex(
      (btn) => btn.rangeType === rangeType && btn.number === number
    );
    return selectedIndex >= 0 ? selectedIndex : 0;
  }

  _selectDataSourceTypeBySchema(schema) {
    switch (schema) {
      case Constants.SCHEMAS.INTERACTION_ANALYTICS:
        return Constants.DATASOURCE_TYPES.INTERACTION_ANALYTICS;
      case Constants.SCHEMAS.ADHOC:
        return Constants.DATASOURCE_TYPES.ADHOC;
      default:
        return Constants.DATASOURCE_TYPES.PREDICTIVE;
    }
  }

  _drillIn(payload) {
    let additionalArgs = {};
    const dateRangeType = payload.dateRangeType
      ? payload.dateRangeType
      : 'DATE_ONLY';

    if (payload.dateRange) {
      // If we pass in a dateRange object this will override the custom range that is otherwise set
      const rangeType = Constants.dateRangeTypes[payload.dateRange.r];
      const preparedCalendarType = ['GREGORIAN', 'G'].includes(
        payload.dateRange.c
      )
        ? 'G'
        : 'F';

      additionalArgs = {
        calendarType: preparedCalendarType,
        dateRangeType: payload.dateRange.t,
        start_date: payload.dateRange.f,
        end_date: payload.dateRange.l,
        first: payload.dateRange.f,
        last: payload.dateRange.l,
        number: payload.dateRange.n,
        rangeType,
        selected: this._getSelectedDateIndex(
          payload.dateRange.t,
          rangeType,
          payload.dateRange.n
        ),
      };
    }
    const drillSource = payload.isOnCard ? 'dashboard' : 'dashboard:detail';
    this.logEvent(`${drillSource}:drill:${payload.route}`, payload.chartType);
    payload?.setSavedDateFilter({
      calendarType: payload.calendarType,
      start_date: payload.startDate,
      end_date: payload.endDate,
      first: payload.startDate,
      last: payload.endDate,
      dateRangeType: dateRangeType,
      ...additionalArgs,
      rangeType: additionalArgs.rangeType || 'C',
    });

    const { questionKey, answerKey, answers, answerType, openInNewWindow } =
      payload;

    let drillInRoute;

    if (payload.dataSourceType === Constants.DASHBOARD.sourceType.definition) {
      drillInRoute = GenerateRoute('datasources.view', {
        dataSourceType: this._selectDataSourceTypeBySchema(payload.schema),
        dataSourceKey: payload.measurementKey,
        dataSourceView: payload.route,
        backTo: '',
      });
    } else {
      drillInRoute = GenerateRoute('measures.dashboard', {
        measurementKey: payload.measurementKey,
        type: payload.route,
      });
    }
    if (openInNewWindow) {
      window.open('/client' + drillInRoute, '_blank');
    } else {
      const params = new URLSearchParams();
      params.set(
        'backTo',
        `${window.location.pathname.replace('/client', '')}`
      );
      if (!params.has('filterId') && payload.filterId) {
        params.set('filterId', payload.filterId);
      }

      const drillDownFromDatasources = drillInRoute.includes('datasources');
      const operator = drillDownFromDatasources ? '&' : '?';
      this.publish('route:change', {
        route: `${drillInRoute}${operator}${params.toString()}`,
      });
    }

    let newTerms = false;
    if (questionKey && (answerKey || answers)) {
      const filterAnswer =
        answerType === 'PVG'
          ? this._buildTextFilter(payload)
          : this._buildAnswerFilter(payload);
      newTerms = {
        question: {
          label: payload.questionLabel,
          questionKey,
        },
        ...filterAnswer,
      };
    }
    if (newTerms !== false && !isNaN(payload.measurementKey)) {
      this.publish('filters:chip:add', {
        measurementKey: payload.measurementKey,
        ...newTerms,
      });
    } else {
      this.publish('filters:reset');
    }
  }

  _buildAnswerFilter(payload) {
    const { questionKey, answerKey, answers, answerLabel } = payload;
    return {
      filterType: Constants.FILTER_TYPE_QUESTION,
      respType: Constants.QT_DROPDOWN, //TODO - This is unknown at this point
      selectedItems: [
        {
          label: answerLabel,
          value: answers
            ? answers.map((answer) => ({
                answerKey: answer.key,
                questionKey,
              }))
            : {
                answerKey,
                questionKey,
              },
        },
      ],
    };
  }

  _buildTextFilter(payload) {
    return {
      filterType: Constants.FILTER_TYPE_TEXT,
      respType: Constants.QT_TEXT,
      operator: Constants.OPERATOR_TEXT_EQUALS,
      value: payload.answerLabel,
    };
  }

  _setCardSize(payload) {
    const { card, size } = payload;

    const alteredCardPayload = {
      ...card,
      metadata: {
        ...card.metadata,
        attributes: { ...card.metadata.attributes, cardSize: size },
      },
    };

    alteredCardPayload.callback = (updatedCard) => {
      this._updateDashboardCard(updatedCard.dashboardId, alteredCardPayload);
    };

    this._saveCard(alteredCardPayload, true);
  }

  _saveCards(payload) {
    const { cards, dashboardId } = payload;
    const model = new DashboardCardsCollection(cards, {
      clientId: this.getClientId(),
      dashboardId,
    });
    model.save({
      success: (data) => {
        const sortedCards = _.sortBy(data, ['sortOrder']);
        this._updateDashboardCards(dashboardId, sortedCards);
      },
      error: () => {
        // Todo
      },
    });
  }

  _saveCard(payload, doNotRefetch) {
    const { cardId, dashboardId, callback, onError, name, lastUpdatedDate } =
      payload;

    if (cardId === 0) {
      delete payload.cardId;
    }

    const record = addDimensionForKPI(payload);
    this._sanitizeDateRange(record);
    const options = {
      clientId: this.getClientId(),
      dashboardId,
      cardId: payload.cardId,
    };

    const metric = record?.metadata?.attributes?.metrics?.[0];
    const metricsName = metric?.label || Constants.METRICS_LABELS[metric?.type];
    const chartType = record?.metadata?.attributes.chartType;
    const isExistingCard = lastUpdatedDate !== null;
    const metricsNames = Object.values(Constants.METRICS_LABELS);

    if (chartType === 'kpi') {
      const initialChartName =
        this.getString('general.untitled') +
        ' ' +
        this.getString(`dashboards.chartType.kpi`);
      if (
        name === initialChartName ||
        (isExistingCard && name !== metricsName && metricsNames.includes(name))
      ) {
        record.name = metricsName;
      }
    } else if (!name || name.length === 0) {
      record.name = this.getString('general.untitled') + ' Card';
    }

    const model = new DashboardCardModel(record, options);
    model
      .save(null, {
        error: onError,
        doNotAbort: true,
      })
      .then(() => {
        if (!doNotRefetch) {
          if (!record.cardId) {
            this._getCards({
              dashboardId,
              callback: (cards) => {
                this._saveCardSortOrders({ dashboardId });
                this._updateDashboardCards(
                  dashboardId,
                  cards.map((card, index) => {
                    return {
                      ...card,
                      sortOrder: index,
                    };
                  })
                );
                if (callback) {
                  callback(model.toJSON());
                }
              },
            });
          } else {
            this._updateDashboardCards(dashboardId, false, () => {
              if (callback) {
                callback(model.toJSON());
              }
            });
          }
        } else if (callback) {
          callback(model.toJSON());
        }
      });
  }

  _sanitizeDateRange(payload) {
    const { dateRange } = payload?.metadata?.attributes || {};
    // if it's a relative date range, then remove any resolved first
    // and last fields from the payload
    if (dateRange && dateRange.r !== 'C' && dateRange.r !== 'CUSTOM') {
      dateRange.f = undefined;
      dateRange.l = undefined;
    }
  }

  _deleteCard(payload) {
    const { cardId, dashboardId, doNotAbort, redirectRoute } = payload;
    const model = new DashboardCardModel(
      {
        cardId: cardId,
      },
      {
        clientId: this.getClientId(),
        dashboardId,
        cardId,
      }
    );

    model.destroy({
      success: (r) => {
        this._getCards({ dashboardId });
        if (payload.callback) {
          payload.callback();
        }
        if (redirectRoute) {
          this.changeRoute('dashboards.summary', { dashboardId });
          this.publish('app:messageFlash', {
            messageText: this.getString('dashboards.snackbar.deleteCard'),
            messageType: 'info',
          });
        }
      },
      // TODO
      // error: (r) => {},
      doNotAbort,
    });
  }

  _postCard(payload) {
    this._saveCard(payload);
  }

  _copyCard(payload) {
    const {
      dashboardId,
      cardId,
      name,
      metadata,
      action,
      destinationDashboard,
      globalDateContext,
      callback,
    } = payload;
    const model = new DashboardCardCopyModel(false, {
      dashboardId,
      cardId,
      destinationDashboard,
      clientId: this.getClientId(),
    });
    model
      .save(null, {
        error: (err, response) => {
          // no cleaner way of doing this at present
          const isAtLimit = response?.responseJSON?.message?.startsWith(
            'Could not copy card as there is 50 or more cards'
          );
          const messageText = isAtLimit
            ? this.getString('dashboards.copyLimit')
            : this.getString('general.errors.default');
          const messageType = isAtLimit ? 'info' : 'error';
          this.publish('app:messageFlash', {
            messageText,
            messageType,
          });
          if (callback) {
            callback(err);
          }
        },
      })
      .then((card) => {
        card.name = name;
        if (metadata && metadata.attributes) {
          const clonedMetaData = cloneDeep(metadata);
          const { filters } = card.metadata.attributes;
          if (filters && filters.length > 0) {
            clonedMetaData.attributes.filters = filters;
          }
          card.metadata = clonedMetaData;
        }

        if (
          globalDateContext?.isEnabled &&
          globalDateContext.dateRange &&
          card.metadata.attributes.dateRange
        ) {
          card.metadata.attributes.dateRange = {
            ...globalDateContext.dateRange,
          };
          card.metadata.attributes.selectedDateId =
            globalDateContext.dateRange.selectedDateId;
        }

        if (card.metadata && card.metadata.attributes) {
          //Copied cards should not have any data source lock restrictions.
          card.metadata.attributes.datasourceLocked = false;
        }
        card.callback = (c) => {
          if (action === 'move') {
            this._deleteCard({
              dashboardId,
              cardId,
              doNotAbort: true,
              redirectRoute: false,
            });
          }

          this.changeRoute('dashboards.summary', {
            dashboardId: destinationDashboard,
          });

          const string =
            action === 'move'
              ? 'dashboards.snackbar.moveCard'
              : 'dashboards.snackbar.copyCard';
          this.publish('app:messageFlash', {
            messageText: this.getString(string),
            messageType: 'info',
          });
        };
        this._saveCard(card);
      });
  }

  _changeDashboardOwner(payload) {
    const { dashboardId, currentOwnerId, changeOwnerId, callback } = payload;
    const model = new DashboardModel(false, {
      clientId: this.getClientId(),
      id: dashboardId,
      changeOwnerId: changeOwnerId,
    });

    // Make current owner a Write Share
    const sharePayload = {
      accessType: 'WRITE',
      shareType: 'USER',
      userId: currentOwnerId,
      dashboardId,
      callback: (writeShare) => {
        model.save({}).then((changedOwner) => {
          if (callback) {
            callback();
          }
          this._get({});
          this._fetchDashboardsShares([{ dashboardId }]);
        });
      },
    };
    this._share(sharePayload);
  }
}

export default DashboardsController;
